import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { PAYPAL_LINKS, apiClient } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    key: "basic",
    name: "CAMO Basic",
    price: "€149",
    period: "/month",
    prefix: "",
    desc: "Designed for creators, professionals and small businesses seeking access to CAMO's essential capabilities.",
    cta: "Join Basic",
    sections: [
      {
        title: "CAMO Growth",
        items: [
          "Reach & Visibility",
          "Basic Content Amplification",
          "Initial Brand Development",
          "Strategic Growth Guidance",
        ],
      },
      {
        title: "CAMO Intelligence",
        items: [
          "Basic Open-Source Research",
          "Preliminary Information Verification",
          "Digital Presence Analysis",
          "Limited Intelligence Consultations",
        ],
      },
      {
        title: "CAMO Protection",
        items: [
          "Basic Reputation Monitoring",
          "Incident Guidance",
          "Security Recommendations",
          "Initial Risk Assessment",
        ],
      },
      {
        title: "Additional Benefits",
        items: [
          "Priority Support",
          "Monthly Review",
          "Reduced Fees on Additional Operations",
        ],
      },
    ],
  },
  {
    key: "professional",
    name: "CAMO Professional",
    price: "€499",
    period: "/month",
    prefix: "",
    featured: true,
    desc: "For businesses, brands and professionals requiring active operational support.",
    cta: "Join Professional",
    sections: [
      {
        title: "CAMO Growth",
        items: [
          "Advanced Growth Strategies",
          "Priority Content Amplification",
          "Digital Positioning Optimization",
          "Continuous Brand Development",
        ],
      },
      {
        title: "CAMO Intelligence",
        items: [
          "Expanded Open-Source Intelligence",
          "Basic Due Diligence",
          "Strategic Market Analysis",
          "Intelligence Reports",
        ],
      },
      {
        title: "CAMO Protection",
        items: [
          "Active Reputation Protection",
          "Incident Management",
          "Advanced Monitoring",
          "Periodic Risk Assessments",
        ],
      },
      {
        title: "Additional Benefits",
        items: [
          "Priority Response",
          "Monthly Reports",
          "Preferred Pricing on Operations",
          "Continuous Strategic Support",
        ],
      },
    ],
  },
  {
    key: "executive",
    name: "CAMO Executive",
    price: "€1,499",
    period: "/month",
    prefix: "Starting at",
    desc: "Premium operational support for organizations, executives and high-profile individuals.",
    cta: "Apply for Executive",
    sections: [
      {
        title: "CAMO Growth",
        items: [
          "Full Growth Management",
          "Expansion Strategies",
          "High-Impact Amplification",
          "Influence Development",
        ],
      },
      {
        title: "CAMO Intelligence",
        items: [
          "Advanced Due Diligence",
          "Complex Strategic Research",
          "Risk Analysis",
          "Executive Reports",
        ],
      },
      {
        title: "CAMO Protection",
        items: [
          "High-Level Reputation Protection",
          "Priority Crisis Management",
          "Continuous Monitoring",
          "Advanced Risk Mitigation",
        ],
      },
      {
        title: "Additional Benefits",
        items: [
          "Dedicated Account Manager",
          "Highest Priority Response",
          "Access to CAMO Network",
          "Custom Operations",
        ],
      },
    ],
  },
];

export const Membership = () => {
  const handleSubscribe = async (plan) => {
    // Lightweight intent log — non-blocking
    try {
      await apiClient.post("/status", { client_name: `intent:${plan.key}` });
    } catch (e) {
      // silent — don't block PayPal redirect
    }
    toast.success(`Redirecting to PayPal — ${plan.name}`);
    setTimeout(() => {
      window.open(PAYPAL_LINKS[plan.key], "_blank", "noopener,noreferrer");
    }, 400);
  };

  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="relative bg-black text-white py-24 md:py-32 lg:py-40 border-t border-zinc-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-zinc-600" />
            <span className="font-mono text-[10px] md:text-xs tracking-[0.4em] uppercase text-zinc-400">
              Membership
            </span>
            <span className="h-px w-10 bg-zinc-600" />
          </div>
          <h2 className="font-display font-light uppercase leading-[1] tracking-tight text-4xl sm:text-5xl md:text-6xl">
            Membership Plans
          </h2>
          <p className="mt-8 font-sans text-base md:text-lg text-zinc-400 leading-relaxed">
            Subscription-based access to a unified framework for growth,
            intelligence and protection.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900">
          {PLANS.map((plan) => (
            <article
              key={plan.key}
              data-testid={`membership-pricing-card-${plan.key}`}
              className={`relative bg-black p-8 md:p-10 flex flex-col ${
                plan.featured ? "lg:-my-6 lg:py-16 bg-zinc-950 z-10" : ""
              }`}
            >
              {plan.featured && (
                <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2">
                  <span className="bg-white text-black font-mono text-[10px] tracking-[0.4em] uppercase px-4 py-2">
                    Most Selected
                  </span>
                </div>
              )}

              <div className="mb-8">
                <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">
                  {plan.name.split(" ")[1]}
                </span>
                <h3 className="mt-4 font-display font-light text-3xl md:text-4xl uppercase tracking-tight">
                  {plan.name}
                </h3>
              </div>

              <div className="pb-8 border-b border-zinc-900">
                {plan.prefix && (
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
                    {plan.prefix}
                  </div>
                )}
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl md:text-6xl text-white">
                    {plan.price}
                  </span>
                  <span className="font-mono text-xs tracking-widest text-zinc-500">
                    {plan.period}
                  </span>
                </div>
                <p className="mt-5 font-sans text-sm text-zinc-400 leading-relaxed">
                  {plan.desc}
                </p>
              </div>

              <div className="py-8 flex-1 space-y-6">
                {plan.sections.map((sec) => (
                  <div key={sec.title}>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                      {sec.title}
                    </div>
                    <ul className="space-y-2">
                      {sec.items.map((it) => (
                        <li
                          key={it}
                          className="flex items-start gap-3 text-sm text-zinc-300 font-sans"
                        >
                          <Check
                            size={14}
                            strokeWidth={1.5}
                            className="text-white mt-1 shrink-0"
                          />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <button
                data-testid={`membership-paypal-button-${plan.key}`}
                onClick={() => handleSubscribe(plan)}
                className={`group mt-4 flex items-center justify-between gap-6 font-mono uppercase text-[11px] tracking-[0.3em] px-6 py-5 transition-colors duration-300 ${
                  plan.featured
                    ? "bg-white text-black hover:bg-zinc-200"
                    : "border border-white text-white hover:bg-white hover:text-black"
                }`}
              >
                <span>{plan.cta}</span>
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </article>
          ))}
        </div>

        <p className="mt-12 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          All subscriptions processed exclusively through PayPal · paypal.me/Felopic
        </p>
      </div>
    </section>
  );
};

export default Membership;
